import { lazy, Suspense, useState } from 'react';
import { LoaderCircle, Maximize } from 'lucide-react';
import { ShareQrErrorBoundary } from '@components/share/ShareQrErrorBoundary';
import { cn } from '@lib/utils';
import { LIVE_POLL_COPY } from '../livePollCopy';
import { useStageBox } from './BigScreenStage';

// Lazy on purpose. The feed's live poll card imports this file for PlaceLogo and the author bar for
// StageHitArea, so everything here reaches Home and loads for every visitor. A static QR import would
// bring qr-code-styling, qrcode and dompurify with it (the same reason as ShareQrPanel in SocialShareSheet).
const BigScreenQrImage = lazy(() =>
  import('./BigScreenQrImage').then(m => ({ default: m.BigScreenQrImage })),
);

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');

/** The Space's (or Event's) logo, or its initials on the teal-deep tile the frames draw. */
export function PlaceLogo({
  name,
  imageUrl,
  className,
  initialsClassName,
}: {
  name: string;
  imageUrl: string | null;
  className: string;
  initialsClassName: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = imageUrl !== null && imageUrl !== '' && failedUrl !== imageUrl;
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden bg-mb-teal-deep',
        className,
      )}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => {
            setFailedUrl(imageUrl);
          }}
        />
      ) : (
        <span className={cn('font-fira font-semibold text-white', initialsClassName)}>
          {initialsOf(name)}
        </span>
      )}
    </div>
  );
}

/**
 * The app's own QR (`generateQrSvg` through `useQrSvg`: rounded modules, the meetball lockup), at a fixed
 * size. Hidden from assistive tech: the typed link sits next to every QR.
 *
 * The box is drawn at once and holds its size while the QR's chunk loads. If the chunk fails to load
 * (venue Wi-Fi, or a post-deploy 404) the box stays empty and the rest of the screen carries on, the typed
 * link included: without this boundary the failure reaches the app root's, which replaces the projected
 * screen with a full-screen reload.
 */
export function BigScreenQr({ link, size }: { link: string; size: number }) {
  return (
    <div
      style={{ width: size, height: size }}
      className="shrink-0 bg-white"
      aria-hidden="true"
      data-testid="big-screen-qr"
    >
      <ShareQrErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <BigScreenQrImage link={link} />
        </Suspense>
      </ShareQrErrorBoundary>
    </div>
  );
}

/**
 * The typed link, kept on one line where it fits; if a narrow caption must break it, it breaks only before
 * the code (as the B2c frame draws it).
 */
export function TypedLink({ display }: { display: string }) {
  const cut = display.lastIndexOf('/') + 1;
  return (
    <span className="whitespace-nowrap" data-testid="typed-link">
      {display.slice(0, cut)}
      <wbr />
      {display.slice(cut)}
    </span>
  );
}

/** E9b: the last state stays on screen; this chip says the screen is catching up (RESOLUTIONS L23). */
export function ReconnectingChip() {
  return (
    <span
      role="status"
      className="inline-flex shrink-0 items-center gap-2.5 rounded-full bg-mb-yellow-pale px-4 py-2 font-fira text-[20px] font-semibold leading-6 text-[#7A4F00]"
    >
      {/* #7A4F00: Paper's warning ink, no token in tailwind.css (a token gap). */}
      <LoaderCircle
        className="h-5 w-5 animate-spin motion-reduce:animate-none"
        strokeWidth={2}
        aria-hidden="true"
      />
      {LIVE_POLL_COPY.reconnecting}
    </span>
  );
}

/** The quiet "Full screen" pill (B1, RESOLUTIONS L13, L15: the frame's 14px, with a 44px hit area). */
export function FullScreenPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-6 top-6 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#FFFFFF8C] px-3 py-1.5 font-fira text-[14px] font-medium leading-[18px] text-mb-charcoal before:absolute before:-inset-x-1 before:-inset-y-[7px] before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
    >
      <Maximize className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
      {LIVE_POLL_COPY.fullScreen}
    </button>
  );
}

/** One result bar: track, teal fill at the share, label and percent above it. */
export function ShareBar({
  label,
  share,
  className,
  labelClassName,
  shareClassName,
}: {
  label: string;
  share: number;
  className: string;
  labelClassName: string;
  shareClassName: string;
}) {
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-between overflow-hidden bg-mb-progress-track',
        className,
      )}
    >
      <div
        className="absolute inset-y-0 left-0 bg-mb-teal transition-[width] duration-300 motion-reduce:transition-none"
        style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
        aria-hidden="true"
      />
      <span
        className={cn(
          'relative min-w-0 flex-1 break-words font-fira font-semibold text-mb-ink',
          labelClassName,
        )}
      >
        {label}
      </span>
      <span className={cn('relative shrink-0 font-fira font-bold text-mb-ink', shareClassName)}>
        {LIVE_POLL_COPY.share(share)}
      </span>
    </div>
  );
}

/**
 * A transparent 44px-tall hit area for a control drawn on the scaled stage (L8, L15): 44px on screen at any
 * scale (taller in stage units when the stage is scaled down, as at 1024×768). Put it inside a `relative`
 * control.
 */
export function StageHitArea() {
  const { scale } = useStageBox();
  const height = Math.max(44, 44 / (scale > 0 ? scale : 1));
  return (
    <span
      aria-hidden="true"
      className="absolute inset-x-0 top-1/2 -translate-y-1/2"
      style={{ height }}
      data-testid="stage-hit-area"
    />
  );
}
